// JournalEntryPrintPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import journalEntryAPI from "../../api/journalEntriesAPI";
import { useAuth } from "../../context/AuthContext"; // ✅ adjust path if needed

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-PH", { year: "numeric", month: "short", day: "2-digit" });
}

function formatMoney(value) {
  const n = Number(value || 0);
  return n.toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function safeStr(v) {
  return v === null || v === undefined ? "" : String(v);
}

export default function JournalEntryPrintPage() {
  const { id } = useParams();
  const { isAuthReady } = useAuth();
  const [loading, setLoading] = useState(true);
  const [entry, setEntry] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    async function load() {
      try {
        setLoading(true);
        setError("");

        // ✅ assumes you have something like journalEntryAPI.getById(id)
        const res = await journalEntryAPI.getById(id);

        // Support either {success:true, data:...} or raw object
        const data = res?.data ?? res;
        if (isMounted) setEntry(data);
      } catch (e) {
        if (isMounted) setError(safeStr(e) || "Failed to load journal entry");
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    if (id && isAuthReady) load();

    return () => {
      isMounted = false;
    };
  }, [id, isAuthReady]);

  // ✅ Set title first, then open print dialog
  useEffect(() => {
    if (loading || !entry || error) return undefined;

    const previousTitle = document.title;
    const dateRaw = entry?.transactionDate || entry?.date || entry?.createdAt || Date.now();
    const date = new Date(dateRaw);
    const yyyymmdd = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(
      date.getDate()
    ).padStart(2, '0')}`;
    const entryNo = String(entry?.entryNumber || entry?.referenceNumber || 'NA').replace(/[^a-zA-Z0-9-_]/g, '_');

    document.title = `JournalEntry-${entryNo}-${yyyymmdd}.pdf`;

    // Ensure title is set before printing
    const printTimer = setTimeout(() => {
      window.print();
    }, 100);

    return () => {
      clearTimeout(printTimer);
      document.title = previousTitle;
    };
  }, [loading, entry, error]);

  const lines = useMemo(() => {
    const raw = entry?.lines || entry?.journalLines || [];
    return Array.isArray(raw) ? raw : [];
  }, [entry]);

  const totals = useMemo(() => {
    const totalDebit = lines.reduce((sum, l) => sum + Number(l?.debit || 0), 0);
    const totalCredit = lines.reduce((sum, l) => sum + Number(l?.credit || 0), 0);
    return {
      totalDebit,
      totalCredit
    };
  }, [lines]);

  if (loading) {
    return (
      <div className="p-6 text-sm">
        Loading print view...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-sm text-red-600">
        {error}
      </div>
    );
  }

  if (!entry) {
    return (
      <div className="p-6 text-sm">
        Journal entry not found.
      </div>
    );
  }

  const client = entry?.accountingClient || entry?.client || null;
  const clientName = client?.name || client?.companyName || "";
  const clientAddress = client?.address || client?.companyAddress || "";
  const clientTin = client?.tin || client?.taxId || "";

  const status = safeStr(entry?.status).toLowerCase();
  const isVoided = status === "void" || status === "voided";

  const entryNumber = entry?.entryNumber || entry?.number || "";
  const referenceNumber = entry?.referenceNumber || "";
  const transactionDate = entry?.transactionDate || entry?.date || entry?.createdAt || "";
  const description = entry?.description || "";

  const postedByName = entry?.postedBy?.name || "";
  const voidedByName = entry?.voidedBy?.name || "";
  const voidReason = entry?.voidReason || "";
  const voidedDate = entry?.voidedDate || "";

  return (
    <div className="p-4">
      {/* Print-only CSS */}
      <style>{`
        @media print {
          @page { size: auto; margin: 12mm; }
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="no-print mb-4 flex items-center gap-2">
        <button
          type="button"
          className="px-3 py-2 rounded border text-sm"
          onClick={() => window.print()}
        >
          Print
        </button>
        <button
          type="button"
          className="px-3 py-2 rounded border text-sm"
          onClick={() => window.history.back()}
        >
          Back
        </button>
      </div>

      <div className="print-container relative p-8 text-sm border rounded">
        {/* ✅ Watermark for voided entries */}
        {isVoided && (
          <div
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            style={{ zIndex: 0 }}
          >
            <div
              style={{
                fontSize: "120px",
                fontWeight: "bold",
                color: "rgba(200, 0, 0, 0.08)",
                transform: "rotate(-30deg)",
                whiteSpace: "nowrap"
              }}
            >
              VOIDED
            </div>
          </div>
        )}

        {/* ✅ Content above watermark */}
        <div className="relative z-10">
          {/* Header */}
          <div className="flex items-start justify-between gap-6">
            <div>
              <div className="text-lg font-semibold">
                {clientName || "Accounting Client"}
              </div>
              {clientAddress ? <div className="text-xs text-gray-700">{clientAddress}</div> : null}
              {clientTin ? <div className="text-xs text-gray-700">TIN: {clientTin}</div> : null}
            </div>

            <div className="text-right">
              <div className="text-xl font-bold">Journal Entry</div>
              <div className="text-xs text-gray-700">
                JE No: <span className="font-semibold">{entryNumber}</span>
              </div>
              {referenceNumber ? (
                <div className="text-xs text-gray-700">
                  Ref No: <span className="font-semibold">{referenceNumber}</span>
                </div>
              ) : null}
              <div className="text-xs text-gray-700">
                Date: <span className="font-semibold">{formatDate(transactionDate)}</span>
              </div>
              <div className="text-xs text-gray-700">
                Status: <span className="font-semibold uppercase">{status || "draft"}</span>
              </div>
            </div>
          </div>

          {/* Meta */}
          {description ? (
            <div className="mt-4">
              <div className="text-xs text-gray-700">Memo / Description</div>
              <div className="text-sm">{description}</div>
            </div>
          ) : null}

          {/* Voided banner info */}
          {isVoided && (
            <div className="mt-4 border rounded p-3">
              <div className="text-sm font-semibold">Voided Information</div>
              {voidedDate ? (
                <div className="text-xs text-gray-700">Voided Date: {formatDate(voidedDate)}</div>
              ) : null}
              {voidedByName ? (
                <div className="text-xs text-gray-700">Voided By: {voidedByName}</div>
              ) : null}
              {voidReason ? (
                <div className="text-xs text-gray-700">Reason: {voidReason}</div>
              ) : (
                <div className="text-xs text-gray-500">Reason: (not provided)</div>
              )}
            </div>
          )}

          {/* Lines Table */}
          <div className="mt-6">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="border px-2 py-2 text-left text-xs">Account</th>
                  <th className="border px-2 py-2 text-left text-xs">Description</th>
                  <th className="border px-2 py-2 text-right text-xs">Debit</th>
                  <th className="border px-2 py-2 text-right text-xs">Credit</th>
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 ? (
                  <tr>
                    <td className="border px-2 py-3 text-center text-xs text-gray-500" colSpan={4}>
                      No lines found.
                    </td>
                  </tr>
                ) : (
                  lines.map((line, idx) => {
                    const acc = line?.account || {};
                    const accLabel = [
                      safeStr(acc?.accountNumber),
                      safeStr(acc?.accountName)
                    ]
                      .filter(Boolean)
                      .join(" - ");

                    return (
                      <tr key={line?._id || idx}>
                        <td className="border px-2 py-2 align-top">
                          <div className="text-xs font-medium">{accLabel || "N/A"}</div>
                        </td>
                        <td className="border px-2 py-2 align-top">
                          <div className="text-xs">{line?.description || ""}</div>
                        </td>
                        <td className="border px-2 py-2 text-right align-top">
                          <div className="text-xs">{formatMoney(line?.debit || 0)}</div>
                        </td>
                        <td className="border px-2 py-2 text-right align-top">
                          <div className="text-xs">{formatMoney(line?.credit || 0)}</div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td className="border px-2 py-2 text-right font-semibold" colSpan={2}>
                    Total
                  </td>
                  <td className="border px-2 py-2 text-right font-semibold">
                    {formatMoney(totals.totalDebit)}
                  </td>
                  <td className="border px-2 py-2 text-right font-semibold">
                    {formatMoney(totals.totalCredit)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Footer */}
          <div className="mt-6 flex items-end justify-between gap-6">
            <div className="text-xs text-gray-700">
              {postedByName ? (
                <div>Posted By: <span className="font-medium">{postedByName}</span></div>
              ) : null}
            </div>

            <div className="text-xs text-gray-700 text-right">
              <div>Generated: {formatDate(new Date())}</div>
            </div>
          </div>

          {/* Signature lines (optional, QB-ish feel) */}
          <div className="mt-10 grid grid-cols-3 gap-6">
            <div>
              <div className="border-t pt-2 text-xs text-gray-700">Prepared By</div>
            </div>
            <div>
              <div className="border-t pt-2 text-xs text-gray-700">Reviewed By</div>
            </div>
            <div>
              <div className="border-t pt-2 text-xs text-gray-700">Approved By</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}